import { assign, get } from "lodash";
import M from "materialize-css";

import { OnlineItemBaseController } from "./online-item-base.controller";
import { thor } from "../../typings/thor";
import { Enums, Events } from "../../thor.enums";
import { MAX_ONLINE_ITEMS } from "../../thor.const";
import { LocaleCode } from "../../typings/lang";
import { PremiumStatus } from "../../typings/common";

export class FutureOnlineItemController extends OnlineItemBaseController {
    public static $inject: string[] = [
        "$element",
        "$log",
        "$mdDialog",
        "$mdMenu",
        "$q",
        "$rootScope",
        "$scope",
        "$translate",
        "categoryService",
        "dateService",
        "itemService",
        "mediaGalleryService",
        "multipleSelect",
        "onlineItemEdit",
        "toaster",
        "utilsService"
    ];

    // binded
    public getTranslationStatus: Function;
    public isMaxItemsReached: boolean;
    public containerGlobalStatus: any; // optional
    public saleStarted: boolean;
    public saleWithoutShipping: boolean;
    public sourceLang: LocaleCode;

    public isItemOnSale: boolean;
    public isItemHideable: boolean;
    public isItemDeletable: boolean;
    public isItemResettable: boolean;
    public descriptionValues: any;

    // binded
    protected isItemEdited: boolean;
    protected readOnly: boolean = false;

    private $t;

    constructor(
        protected $element,
        private $log: ng.ILogService,
        private $mdDialog: ng.material.IDialogService,
        private $mdMenu: ng.material.IMenuService,
        private $q: ng.IQService,
        private $rootScope: ng.IRootScopeService,
        protected $scope: ng.IScope,
        $translate: ng.translate.ITranslateService,
        protected categoryService,
        public dateService,
        private itemService,
        protected mediaGalleryService,
        public multipleSelect,
        protected onlineItemEdit,
        private toaster,
        protected utilsService
    ) {
        super($element, $scope, categoryService, mediaGalleryService, onlineItemEdit);

        this.$t = $translate.instant;
    }

    public $onInit() {
        this.getDescriptionTranslations();

        this.$scope.$watch(
            () => this.item.inView,
            isCardInViewport => {
                if (isCardInViewport) {
                    this.inView = isCardInViewport;

                    angular.element(() => {
                        const dropdown = this.$element[0].querySelector(".dropdown-trigger");
                        if (dropdown) {
                            M.Dropdown?.init(dropdown, {
                                constrainWidth: false
                            });
                        }
                    });

                    angular.element(document).ready(() => {
                        const tooltip = this.$element[0].querySelectorAll(".tooltiped");
                        M.Tooltip.init(tooltip, {});
                    });

                    this._displayImageName(this.multipleSelect.elemIds.length > 0);
                }
            }
        );

        this.$scope.$watch(
            () => this.item,
            () => this.getDescriptionTranslations(),
            true
        );

        this.$scope.$watch(
            () => this.multipleSelect.elemIds.length > 0,
            isMultipleSelectActivated => {
                this._displayImageName(isMultipleSelectActivated);
            }
        );
        this.$scope.$watch(
            () => this.item.status,
            () => {
                this._checkStatusItem();
            }
        );
        this._checkStatusItem();
    }

    /*
        If you click on the tooltiped icon, it doesn't hide the tooltip
        So we programatically remove it
    */
    public $onDestroy() {
        angular.element(document).ready(() => {
            const materialTooltip = document.querySelector(".material-tooltip");
            if (materialTooltip) {
                materialTooltip.remove();
            }
        });
    }

    public addMedia(event, files) {
        this.mediaGalleryService.addMedia(event, files, this.item);
    }

    public reset(event) {
        this.checkMaxItemsReached(event).then(() =>
            this.$mdMenu.hide().then(() =>
                this.itemService
                    .reset(this.item.id)
                    .then(async res => {
                        this.$log.info(`Item ${this.item.id} reset with success`, res);
                        for (const key in res) {
                            if (res.hasOwnProperty(key) && this.item.hasOwnProperty(key)) {
                                this.item[key] = res[key];
                            }
                        }
                        await this.getTranslationStatus();
                        this.$rootScope.$broadcast(Events.OnlineItemUpdated, { fetchItems: true });
                        this.$rootScope.$broadcast(Events.OnlineSalePurged);
                        this.$rootScope.$broadcast(Events.PostMessageToV2);
                    })
                    .catch(error => this.$log.error(`Error while resetting item ${this.item.id}`, error))
            )
        );
    }

    public saveAskedForPremium(data) {
        if (!data.asked_for_premium && this.item.premium_status === PremiumStatus.Confirmed && this.saleStarted) {
            this.toaster.toastPremiumOnSaleStartedOrInLessThan48hours(this.saleStarted);
            return;
        }
        this._updateItem(data);
    }

    public toggleState(event, stateName: Enums.ItemState.Hidden | Enums.ItemState.Suppressed) {
        let title = "";
        let question = "";
        if (!this.item.states.hidden && !this.item.states.suppressed) {
            if (
                this.isItemOnSale &&
                (this.containerGlobalStatus.status === Enums.SaleStatus.Published ||
                    this.containerGlobalStatus.status === Enums.SaleStatus.Edited)
            ) {
                title = this.$t("retirer_des_encheres");
                question = this.$t("vous_allez_definitivement_retirer_des_encheres_ce_lot");
            } else {
                title = this.$t("supprimer_le_lot");
                question = this.$t("vous_allez_supprimer_ce_lot_voulez_vous_continuer");
            }
            this.$mdDialog
                .show(
                    this.$mdDialog
                        .confirm()
                        .title(title)
                        .textContent(question)
                        .ok(this.$t("oui"))
                        .cancel(this.$t("non"))
                )
                .then(() => this._toggleState(stateName));
        } else {
            this.checkMaxItemsReached(event, stateName).then(() => this._toggleState(stateName));
        }
    }

    private checkMaxItemsReached(event, stateName?: Enums.ItemState.Hidden | Enums.ItemState.Suppressed) {
        return this.$q((resolve, reject) => {
            if (this.isMaxItemsReached) {
                const content = this.$t("max_items_add_error", [MAX_ONLINE_ITEMS]);
                const title = stateName
                    ? this.$t(stateName === Enums.ItemState.Hidden ? "annuler_le_retrait" : "annuler_la_suppression")
                    : this.$t(
                          this.isItemOnSale
                              ? "annuler_le_retrait_des_encheres"
                              : "revenir_a_la_derniere_version_publiee"
                      );
                reject(
                    this.$mdDialog.show(
                        this.$mdDialog
                            .alert()
                            .targetEvent(event)
                            .title(title)
                            .textContent(content)
                            .ok(this.$t("j_ai_compris"))
                    )
                );
            }
            resolve();
        });
    }

    private _toggleState(stateName) {
        const states =
            assign(
                this.item.states,
                { [stateName]: !get(this.item.states, stateName, false) }
            );

        const data: thor.ItemPatch = {
            states: states
        };

        this._updateItem(data);
    }

    private _checkStatusItem() {
        const { hasBeenPublishedOnce, saleStarted } = this.containerGlobalStatus || {};
        const isSalePublished = this.containerGlobalStatus.status === Enums.SaleStatus.Published;
        const isItemCreated = this.item.status === Enums.ItemStatus.Created;
        const isItemModified = this.item.status === Enums.ItemStatus.Modified;
        const isItemConfirmed = this.item.status === Enums.ItemStatus.Confirmed;

        // reset to default
        this.isItemResettable = false;
        this.isItemHideable = false;
        this.isItemDeletable = false;

        if (saleStarted && (isItemConfirmed || isItemModified)) {
            this.isItemOnSale = true;
        }

        if (this.isItemOnSale && !this.item.states.suppressed && !this.item.states.hidden) {
            this.isItemHideable = true;
        }

        if ((!isSalePublished || !saleStarted || isItemCreated) && !this.item.states.hidden) {
            this.isItemDeletable = true;
        }

        if ((isSalePublished || hasBeenPublishedOnce) && isItemModified) {
            this.isItemResettable = true;
        }
    }

    private _updateItem(data) {
        this.itemService
            .update(this.item.id, data)
            .then(async res => {
                this.$log.info("Item updated with success", res);
                const pricing = this.item.pricing;
                this.item = assign(this.item, res);
                this.item.pricing = {
                    ...this.item.pricing, // making sure last-bids doesn't get overwritten by the query result
                    ...pricing
                };
                const fetchItems = !this.containerGlobalStatus.saleStarted;

                await this.getTranslationStatus();
                this.$rootScope.$broadcast(Events.OnlineItemUpdated, { fetchItems });
            })
            .catch(error => this.$log.error("Error while updating the item", error));
    }

    private _displayImageName(isMultipleSelectActivated: boolean) {
        angular.element(() => {
            const $imageName = <HTMLSpanElement>this.$element[0].querySelector(".image-name");
            if ($imageName) {
                if (isMultipleSelectActivated) {
                    $imageName.classList.add("show-image-name");
                } else {
                    $imageName.classList.remove("show-image-name");
                }
            }
        });
    }

    private getDescriptionTranslations() {
        this.descriptionValues = {};

        this.descriptionValues[this.sourceLang] = this.item.description || null;

        if (this.item.description_translations) {
            if (this.sourceLang !== LocaleCode.en_US) {
                this.descriptionValues[LocaleCode.en_US] = this.item.description_translations[LocaleCode.en_US] ||
                null;
            }
            if (this.sourceLang !== LocaleCode.fr_FR) {
                this.descriptionValues[LocaleCode.fr_FR] = this.item.description_translations[LocaleCode.fr_FR] ||
                null;
            }
        }
    }
}
